/** See `src/i18n/locales/en/overview.ts`. */
export const overview = {
  page: {
    title: "Vue d'ensemble",
    lead: 'Comment se porte {{company}} ?',
    noScope: "Sélectionnez une entreprise pour voir la vue d'ensemble.",
    noDepartments: 'Aucun département ici ne possède de statistiques que vous pouvez voir.',
    disclaimer:
      'Toutes les valeurs sont des indications de bien-être au niveau du groupe et ne constituent pas un diagnostic médical.',
  },
  summary: {
    caption: 'Résumé IA basé sur des données anonymisées et agrégées.',
  },
  figures: {
    vitality: 'Vitalité',
    stress: 'Stress',
    sleep: 'Sommeil',
    engagement: 'Engagement',
    activeParticipants: 'Participants actifs',
  },
  figuresInfo: {
    vitality:
      "Un score composite basé sur le sommeil, l'activité, la HRV, le stress et la fréquence cardiaque.",
    stress: 'Niveau de stress moyen, mesuré directement par le wearable.',
    sleep: 'Durée moyenne de sommeil par nuit, mesurée directement par le wearable.',
    engagement: "Part des employés utilisant activement l'application.",
    activeParticipants: 'Nombre d’employés actifs au cours de la période écoulée.',
  },
  legend: {
    title: 'Ce que vous voyez',
    measured:
      'Mesuré directement — directement depuis le wearable NOLATR ou un capteur connecté. Fiabilité la plus élevée.',
    aiDerived:
      "Dérivé par IA — pas une mesure directe. L'IA combine plusieurs signaux en un seul indicateur.",
    usage:
      "Usage & comportement — interactions réelles dans l'application, pas une mesure de santé.",
  },
  developments: {
    title: 'Principaux développements',
    empty: 'Aucun développement notable cette période.',
    stress: {
      title: "Opportunité d'offrir un moment de repos (stress élevé)",
      body: "Un moment de détente convivial ou une astuce de respiration peut aider l'équipe à recharger ses batteries.",
    },
    sleep: {
      title: "Opportunité : sommeil en dessous de l'objectif",
      body: "Une astuce de récupération ciblée et positive peut bien soutenir l'équipe.",
    },
    vitalityGood: {
      title: 'La vitalité évolue bien',
      body: "L'équipe maintient bien son rythme actuel — un bon moment pour le reconnaître.",
    },
    needsReactivation: {
      title: '{{count}} personnes doivent être réactivées',
      body: 'Un message de réactivation court et personnel fonctionne généralement le mieux.',
    },
    mostActiveWeekday: {
      title: "Plus d'énergie le {{weekday}}",
      body: 'Planifiez les défis et la communication autour du jour le plus actif pour une portée maximale.',
    },
    challengeParticipation: {
      title: 'Participation au défi {{pct}}%',
      body: "Un nouveau défi d'équipe facile d'accès pourrait inciter davantage de personnes à participer.",
    },
  },
  actions: {
    title: 'Actions pour les RH',
    empty: 'Aucune action recommandée pour le moment.',
    sendReminder: {
      title: '{{count}} employés pas encore activés',
      subtitle: 'Envoyez un rappel',
    },
    reactivate: {
      title: '{{count}} employés doivent être réactivés',
      subtitle: 'Prenez contact',
    },
    stressCheckIn: {
      title: "Stress élevé au sein de l'équipe",
      subtitle: 'Envisagez un point rapide sur la charge de travail',
    },
    sleepReminder: {
      title: "Sommeil en dessous de l'objectif au sein de l'équipe",
      subtitle: "Envisagez un rappel sur les routines du soir et le temps d'écran",
    },
    activeChallenge: {
      title: "'{{name}}' est en cours — planifiez le suivant",
      subtitle: 'Défis',
    },
    pilotEvaluation: {
      interim: {
        title: "Le pilote '{{name}}' est prêt pour son évaluation intermédiaire",
        subtitle: 'Voir le pilote',
      },
      final: {
        title: "Le pilote '{{name}}' est prêt pour son évaluation finale",
        subtitle: 'Voir le pilote',
      },
    },
  },
  energy: {
    title: 'Énergie & bien-être au travail',
    subtitle:
      "Comment l'équipe vit sa journée de travail — énergie, pauses, rythme de travail et engagement. Agrégé uniquement, jamais individuel.",
    badge: 'Agrégé et anonymisé',
    tbd: 'À déterminer',
    missingData: {
      noSource: "Indice d'énergie et eNPS : source de données pas encore déterminée.",
      insufficientParticipants: 'Participants insuffisants pour {{metrics}}.',
    },
    privacyNote:
      "Informations d'accompagnement, pas de surveillance. Bien-être indicatif au niveau du groupe — aucune interprétation médicale.",
    metrics: {
      energyIndex: "Indice d'énergie",
      breakAdoption: 'Adoption des pauses',
      focusRhythm: 'Rythme de concentration',
      coachAdoption: 'Adoption du coach',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Statut des pilotes',
    empty: 'Aucun pilote pour le moment.',
    participantsAndDay: '{{count}} participants · jour {{day}} sur {{total}}',
    viewReport: 'Voir le rapport du pilote',
  },
  challenges: {
    title: 'Défis actifs',
    empty: 'Aucun défi pour le moment.',
    active: 'Actifs',
    total: 'Total',
    participants: 'Participants',
    activeBadge: '{{count}} actifs',
    manageLink: 'Gérer les défis',
    noneActive: 'Aucun défi actif pour le moment.',
    participantsCount: '{{count}} participants',
  },
  trends: {
    title: 'Tendances',
    filters: {
      allDepartments: 'Tous les départements',
      participants: {
        label: 'Participants',
        all: 'Tous les participants',
        manager: 'Managers',
        employee: 'Employés',
      },
    },
    vitalityScore: 'Score de vitalité dans le temps',
    activeUsersPerWeek: 'Utilisateurs actifs par semaine',
    sleep: 'Tendance du sommeil',
    stress: 'Tendance du stress',
    hrv: 'Tendance HRV',
    recovery: 'Tendance de récupération',
    activity: "Tendance d'activité",
    notAvailable:
      'Pas encore de source de données pour cette tendance — non disponible dans cette version.',
    vsPreviousPeriod: 'vs période précédente',
    info: {
      vitalityScore:
        'Un score de vitalité composite par jour, basé sur tous les signaux de vitalité.',
      activeUsersPerWeek:
        "Nombre d'employés uniques ayant utilisé activement l'application chaque semaine.",
      sleep: 'Durée moyenne de sommeil par jour, mesurée directement par le wearable.',
      stress: 'Niveau de stress moyen par jour, mesuré directement par le wearable.',
      hrv: 'Variabilité moyenne de la fréquence cardiaque (HRV) par jour, un indicateur de récupération.',
      recovery:
        "Un score de récupération composite par jour, basé sur le sommeil, la HRV et l'activité.",
      activity: 'Nombre moyen de pas par jour, mesuré directement par le wearable.',
    },
  },
} as const;
